<template>
    <section-wrapper
        :class="Section.RISK_TOLERANCE.cssClassName"
        :this-section="Section.RISK_TOLERANCE"
        :next-section="Section.RISK_TOLERANCE_EVALUATION"
        :section-end-button-label="$t('riskToleranceQuestions.sectionEndButtonLabel')"
        :section-end-button-is-disabled="!allQuestionsAnswered"
        @sectionEndButtonClick="handleSectionEndButtonClick">
        <div class="row">
            <div class="col">
                <h2>{{ $t('riskToleranceQuestions.title') }}</h2>
                <div class="row">
                    <div class="col">
                        <p>
                            {{ $t('riskToleranceQuestions.introText', {riskTolerance:
                                $t(`riskToleranceEvaluation.riskDescription.${selectedStrategyName}`)}) }}
                        </p>
                        <div class="risk-tolerance-questions__block">
                            <p class="risk-tolerance-questions__question">
                                1. {{ $t('riskToleranceQuestions.block1.question') }}
                            </p>
                            <!-- desktop -->
                            <radio-simple
                                v-for="(answer, answerIndex) in question1Answers"
                                class="risk-tolerance-questions__answer d-none d-lg-block"
                                :key="answerIndex"
                                :checked="riskToleranceBlock1SelectedAnswer === answer.key"
                                :text="answer.value"
                                @click="setSelectedAnswer(1, answer.key)" />
                            <!-- mobile -->
                            <risk-tolerance-question-dropdown
                                class="d-lg-none"
                                :selected="riskToleranceBlock1SelectedAnswer"
                                :options="question1Answers"
                                :is-collapsed="question1DropdownIsCollapsed"
                                @toggleCollapse="question1DropdownIsCollapsed = !question1DropdownIsCollapsed"
                                @onSelect="$event => setSelectedAnswer(1, $event)" />
                        </div>
                        <div class="risk-tolerance-questions__block">
                            <p class="risk-tolerance-questions__question">
                                2. {{ $t('riskToleranceQuestions.block2.question', { sellAmount: sellAmountLow, winAmount}) }}
                            </p>
                            <!-- desktop -->
                            <radio-simple
                                v-for="(answer, answerIndex) in question2Answers"
                                class="risk-tolerance-questions__answer d-none d-lg-block"
                                :key="answerIndex"
                                :checked="riskToleranceBlock2SelectedAnswer === answer.key"
                                :text="answer.value"
                                @click="setSelectedAnswer(2, answer.key)" />
                            <!-- mobile -->
                            <risk-tolerance-question-dropdown
                                class="d-lg-none"
                                :selected="riskToleranceBlock2SelectedAnswer"
                                :options="question2Answers"
                                :is-collapsed="question2DropdownIsCollapsed"
                                @toggleCollapse="question2DropdownIsCollapsed = !question2DropdownIsCollapsed"
                                @onSelect="$event => setSelectedAnswer(2, $event)" />
                        </div>

                        <div class="risk-tolerance-questions__block">
                            <p class="risk-tolerance-questions__question">
                                3. {{ $t('riskToleranceQuestions.block3.question') }}
                            </p>
                            <!-- desktop -->
                            <radio-simple
                                v-for="(answer, answerIndex) in question3Answers"
                                class="risk-tolerance-questions__answer d-none d-lg-block"
                                :key="answerIndex"
                                :checked="riskToleranceBlock3SelectedAnswer === answer.key"
                                :text="answer.value"
                                @click="setSelectedAnswer(3, answer.key)" />
                            <!-- mobile -->
                            <risk-tolerance-question-dropdown
                                class="d-lg-none"
                                :selected="riskToleranceBlock3SelectedAnswer"
                                :options="question3Answers"
                                :is-collapsed="question3DropdownIsCollapsed"
                                @toggleCollapse="question3DropdownIsCollapsed = !question3DropdownIsCollapsed"
                                @onSelect="$event => setSelectedAnswer(3, $event)" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section-wrapper>
</template>

<script>
import RadioSimple from '@/components/common/RadioSimple';
import RiskToleranceQuestionDropdown from '@/components/RiskToleranceQuestionDropdown';
import { mapActions, mapState } from 'vuex';
import { RiskToleranceAnswer } from '@/api/enums';
import SectionWrapper from '@/components/common/SectionWrapper';
import { Section } from '@/enums';

export default {
    name: 'RiskToleranceQuestions',
    components: {
        RadioSimple,
        RiskToleranceQuestionDropdown,
        SectionWrapper,
    },
    computed: {
        ...mapState([
            'investmentAmount',
            'riskToleranceBlock1SelectedAnswer',
            'riskToleranceBlock2SelectedAnswer',
            'riskToleranceBlock3SelectedAnswer',
            'investmentStrategySelected']),
        sellAmountLow () {
            return this.$options.filters.formatAmount(Math.max(this.investmentAmountBasis, 10000));
        },
        sellAmountHigh () {
            return this.$options.filters.formatAmount(Math.max(this.investmentAmountBasis * 1.5, 15000));
        },
        winAmount () {
            return this.$options.filters.formatAmount(Math.max(this.investmentAmountBasis * 4, 40000));
        },
        investmentAmountBasis () {
            return Math.round(this.investmentAmount * 0.1 / 5000) * 5000;
        },
        allQuestionsAnswered () {
            return (this.riskToleranceBlock1SelectedAnswer !== RiskToleranceAnswer.NOT_ANSWERED &&
                  this.riskToleranceBlock2SelectedAnswer !== RiskToleranceAnswer.NOT_ANSWERED &&
                  this.riskToleranceBlock3SelectedAnswer !== RiskToleranceAnswer.NOT_ANSWERED);
        },
        question1Answers () {
            return [RiskToleranceAnswer.ANSWER_1,
                RiskToleranceAnswer.ANSWER_2,
                RiskToleranceAnswer.ANSWER_3,
                RiskToleranceAnswer.ANSWER_4].map((riskToleranceAnswer, answerIndex) => {
                return {
                    key: riskToleranceAnswer,
                    value: this.$t(`riskToleranceQuestions.block1.answer${answerIndex + 1}`),
                };
            });
        },
        question2Answers () {
            return [
                {
                    key: RiskToleranceAnswer.ANSWER_1,
                    value: this.$t('riskToleranceQuestions.block2.answer1', { sellAmount: this.sellAmountLow }),
                },
                {
                    key: RiskToleranceAnswer.ANSWER_2,
                    value: this.$t('riskToleranceQuestions.block2.answer2', { sellAmount: this.sellAmountHigh }),
                },
                {
                    key: RiskToleranceAnswer.ANSWER_3,
                    value: this.$t('riskToleranceQuestions.block2.answer3'),
                }];
        },
        question3Answers () {
            return [RiskToleranceAnswer.ANSWER_1,
                RiskToleranceAnswer.ANSWER_2,
                RiskToleranceAnswer.ANSWER_3].map((riskToleranceAnswer, answerIndex) => {
                return {
                    key: riskToleranceAnswer,
                    value: this.$t(`riskToleranceQuestions.block3.answer${answerIndex + 1}`),
                };
            });
        },
        selectedStrategyName () {
            return this.investmentStrategySelected ? this.investmentStrategySelected.name : '';
        },
    },
    methods: {
        ...mapActions([
            'setRiskToleranceBlock1SelectedAnswer',
            'setRiskToleranceBlock2SelectedAnswer',
            'setRiskToleranceBlock3SelectedAnswer',
            'evaluateSurvey',
            'removeOpenSection']),
        setSelectedAnswer (blockNumber, answer) {
            switch (blockNumber) {
                case 1:
                    this.setRiskToleranceBlock1SelectedAnswer(answer);
                    this.question1DropdownIsCollapsed = true;
                    break;
                case 2:
                    this.setRiskToleranceBlock2SelectedAnswer(answer);
                    this.question2DropdownIsCollapsed = true;
                    break;
                case 3:
                    this.setRiskToleranceBlock3SelectedAnswer(answer);
                    this.question3DropdownIsCollapsed = true;
                    break;
            }
        },
        handleSectionEndButtonClick () {
            this.removeOpenSection(Section.RISK_TOLERANCE);
            this.evaluateSurvey();
        },
    },
    data () {
        return {
            Section,
            RiskToleranceAnswer,
            question1DropdownIsCollapsed: false,
            question2DropdownIsCollapsed: true,
            question3DropdownIsCollapsed: true,
        };
    },
};
</script>

<style scoped lang="scss">
  .risk-tolerance-questions__answer {
    padding: 5.5px;
    background: $mb-grey1;
    text-indent: -35px;
    padding-left: 45px;

    @include media-breakpoint-down(xs) {
      line-height: 18px;
    }

    ::v-deep &.radio-simple--checked {
      background: $mb-grey-12;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);

    }
  }

  .risk-tolerance-questions__question {
    margin-top: 30px;
    margin-bottom: 20px;
    text-indent: -1.2em;
    padding-left: 1.2em;
    font-size: 20px;
    line-height: 24px;
    color: $mb-grey5;

    @include media-breakpoint-down(xs) {
      margin-top: 20px;
      margin-bottom: 15px;
      font-size: 14px;
      line-height: 18px;
    }
  }
</style>
